import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar, Box, Button, Group, Text } from '@mantine/core';
import { apiClient } from '../services/api';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { useSquareCanvas } from '../hooks/useSquareCanvas';
import { Game, Player } from '../types/models';
import SVGDrawingBoard, { SVGDrawingBoardHandle } from './SVGDrawingBoard';
import { DrawingControlsMantine } from './DrawingControlsMantine';
import { GameLayout } from './layouts/GameLayout';
import { useDrawingPersistence } from '../hooks/useDrawingPersistence';
import { AVATAR_FULL_SIZE, AVATAR_THUMB_SIZE, createAvatarThumbnailFromBlob } from '../utils/avatarImages';
import { DrawingData, DrawingTool } from '../utils/drawingExport';

interface LobbyAvatarEditorProps {
  game: Game;
  currentPlayer: Player;
  onCancel: () => void;
  onSaved: () => void;
  onHistory?: () => void;
}

const LobbyAvatarEditor: React.FC<LobbyAvatarEditorProps> = ({
  game,
  currentPlayer,
  onCancel,
  onSaved,
  onHistory
}) => {
  const { isMobile } = useResponsiveLayout();
  const { width: canvasSize } = useSquareCanvas({
    padding: isMobile ? 8 : 16,
    reservedTop: isMobile ? 64 : 72,
    reservedBottom: isMobile ? 140 : 96,
    maxSize: isMobile ? 600 : 800
  });
  const drawingRef = useRef<SVGDrawingBoardHandle | null>(null);
  const [boardReady, setBoardReady] = useState(false);
  const [avatarLoaded, setAvatarLoaded] = useState(false);
  const [currentColor, setCurrentColor] = useState('#000000');
  const [currentTool, setCurrentTool] = useState<DrawingTool>('pen');
  const [currentSize, setCurrentSize] = useState(5);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [strokeCount, setStrokeCount] = useState(0);
  const [hasBackground, setHasBackground] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { loadDrawing, saveDrawing, clearDrawing } = useDrawingPersistence({
    gameKey: game.key,
    stackId: `avatar-${currentPlayer.id}`,
    entryNumber: 0
  });

  const handleBoardRef = useCallback((node: SVGDrawingBoardHandle | null) => {
    drawingRef.current = node;
    setBoardReady(!!node);
  }, []);

  useEffect(() => {
    if (currentPlayer.avatarUrl) {
      localStorage.setItem('userAvatarUrl', currentPlayer.avatarUrl);
    }
    if (currentPlayer.avatarThumbUrl) {
      localStorage.setItem('userAvatarThumbUrl', currentPlayer.avatarThumbUrl);
    }
  }, [currentPlayer.avatarUrl, currentPlayer.avatarThumbUrl]);

  useEffect(() => {
    if (!boardReady || avatarLoaded) return;

    let cancelled = false;
    const loadExistingAvatar = async () => {
      const saved = await loadDrawing();
      if (!cancelled && saved) {
        drawingRef.current?.loadDrawingData(saved);
        setHasBackground(!!saved.backgroundDataUrl);
        setAvatarLoaded(true);
        return;
      }

      if (!currentPlayer.avatarUrl) {
        setAvatarLoaded(true);
        return;
      }

      try {
        const response = await fetch(currentPlayer.avatarUrl);
        if (!response.ok) {
          throw new Error('Failed to load avatar');
        }

        const blob = await response.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('Failed to read avatar'));
          reader.readAsDataURL(blob);
        });

        if (cancelled) return;
        drawingRef.current?.setBackgroundImage(dataUrl);
        setHasBackground(true);
      } catch (loadError) {
        console.warn('Unable to preload avatar', loadError);
      } finally {
        if (!cancelled) {
          setAvatarLoaded(true);
        }
      }
    };

    loadExistingAvatar();

    return () => {
      cancelled = true;
    };
  }, [avatarLoaded, boardReady, currentPlayer.avatarUrl, loadDrawing]);

  const handleAvatarSave = () => {
    if (isSaving || strokeCount < 1) return;
    setError(null);
    drawingRef.current?.handleSave();
  };

  const handleAvatarClear = () => {
    drawingRef.current?.clear();
    setError(null);
    setHasBackground(false);
    setCanUndo(false);
    setCanRedo(false);
  };

  const handleAvatarPersist = useCallback((drawingData: DrawingData | null) => {
    if (drawingData) {
      saveDrawing(drawingData);
      setHasBackground(!!drawingData.backgroundDataUrl);
      return;
    }
    clearDrawing();
    setHasBackground(false);
  }, [clearDrawing, saveDrawing]);

  const handleAvatarSubmit = useCallback(async (blob: Blob) => {
    setIsSaving(true);
    setError(null);

    try {
      const thumbnailBlob = await createAvatarThumbnailFromBlob(blob, AVATAR_THUMB_SIZE);
      const [fullResponse, thumbResponse] = await Promise.all([
        apiClient.uploadDrawing(blob),
        apiClient.uploadDrawing(thumbnailBlob)
      ]);

      await apiClient.updatePlayer(game.key, currentPlayer.id, {
        avatarUrl: fullResponse.data.url,
        avatarThumbUrl: thumbResponse.data.url
      });

      localStorage.setItem('userAvatarUrl', fullResponse.data.url);
      localStorage.setItem('userAvatarThumbUrl', thumbResponse.data.url);
      onSaved();
    } catch (err) {
      const errorMessage =
        typeof err === 'object' && err !== null && 'error' in err
          ? String((err as { error?: string }).error || '')
          : err instanceof Error
            ? err.message
            : '';
      setError(errorMessage || 'Failed to update avatar. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [game.key, currentPlayer.id, onSaved]);

  const rosterLimit = isMobile ? 4 : 6;
  const visiblePlayers = game.players.slice(0, rosterLimit);
  const remainingCount = game.players.length - visiblePlayers.length;

  const header = (
    <Box>
      <Group gap="sm" justify="space-between" align="center" wrap="nowrap">
        <Button
          variant="light"
          size={isMobile ? 'sm' : 'md'}
          onClick={onCancel}
          disabled={isSaving}
          style={{ flexShrink: 0 }}
        >
          Cancel
        </Button>
        <Group gap="xs" justify="center" align="center" wrap="nowrap" style={{ flex: 1 }}>
          {visiblePlayers.map((player) => (
            <Avatar
              key={player.id}
              src={player.avatarThumbUrl || player.avatarUrl || undefined}
              radius="xl"
              size={isMobile ? 28 : 32}
              color="blue"
            >
              {player.name?.charAt(0)?.toUpperCase() || '?'}
            </Avatar>
          ))}
          {remainingCount > 0 && (
            <Text size="sm" c="dimmed">
              +{remainingCount}
            </Text>
          )}
        </Group>
        <Button
          size={isMobile ? 'sm' : 'md'}
          onClick={handleAvatarSave}
          loading={isSaving}
          disabled={strokeCount < 1}
          style={{ flexShrink: 0 }}
        >
          Save
        </Button>
      </Group>
      {currentPlayer.discordUserId && onHistory && (
        <Group justify="center" mt="xs">
          <Button
            variant="subtle"
            size={isMobile ? 'xs' : 'sm'}
            onClick={onHistory}
          >
            Avatar history
          </Button>
        </Group>
      )}
      {error && (
        <Text c="red" size="sm" ta="center" mt="xs">
          {error}
        </Text>
      )}
    </Box>
  );

  const content = (
    <Box
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {canvasSize > 0 && (
        <Box style={{ width: `${canvasSize}px`, height: `${canvasSize}px` }}>
          <SVGDrawingBoard
            ref={handleBoardRef}
            width={AVATAR_FULL_SIZE}
            height={AVATAR_FULL_SIZE}
            onSave={handleAvatarSubmit}
            onPersist={handleAvatarPersist}
            currentColor={currentColor}
            currentTool={currentTool}
            strokeWidth={currentSize}
            onCanUndoChange={setCanUndo}
            onCanRedoChange={setCanRedo}
            onStrokeCountChange={setStrokeCount}
          />
        </Box>
      )}
    </Box>
  );

  const controls = (
    <DrawingControlsMantine
      currentColor={currentColor}
      currentTool={currentTool}
      currentSize={currentSize}
      onColorChange={setCurrentColor}
      onSizeChange={setCurrentSize}
      onToolChange={setCurrentTool}
      onUndo={() => drawingRef.current?.handleUndo()}
      onRedo={() => drawingRef.current?.handleRedo()}
      canUndo={canUndo}
      canRedo={canRedo}
      onClear={handleAvatarClear}
      canClear={strokeCount > 0 || hasBackground}
      showEraser
    />
  );

  return (
    <GameLayout
      header={header}
      content={content}
      controls={controls}
      mode="draw"
    />
  );
};

export default LobbyAvatarEditor;
